import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Upload, FileXls, Download, CheckCircle, X } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ImportPlanningDialog({ open, onOpenChange, chantiers, defaultChantierId, onImportSuccess }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [chantierId, setChantierId] = useState(defaultChantierId || '');
  const [mode, setMode] = useState('add');
  const [submitting, setSubmitting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setMode('add');
      if (defaultChantierId && defaultChantierId !== 'all') setChantierId(defaultChantierId);
    }
  }, [open, defaultChantierId]);

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const res = await axios.get(`${API}/planning/template/excel`, {
        responseType: 'blob', withCredentials: true,
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'CHANTIX_Template_Planning.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Template Excel téléchargé');
    } catch {
      toast.error('Erreur lors du téléchargement du template');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    if (!chantierId || chantierId === 'all') {
      toast.error('Veuillez sélectionner un chantier');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('chantier_id', chantierId);
      formData.append('mode', mode);
      const res = await axios.post(`${API}/planning/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      if (res.data.success) {
        toast.success(`Import OK : ${res.data.tasks_created} tâches, ${res.data.lots_created} lots créés`);
        if (onImportSuccess) onImportSuccess();
      } else {
        toast.error('Import échoué — voir détails ci-dessous');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="import-planning-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={22} weight="bold" className="text-[#0055FF]" />
            Importer un planning
          </DialogTitle>
          <DialogDescription>
            Excel libre (.xlsx) ou MS Project (.xml). Les colonnes obligatoires : tâche, date début, date fin.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            {/* Bouton template */}
            <Button
              type="button"
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloadingTemplate}
              className="w-full justify-start text-sm"
              data-testid="download-template-btn"
            >
              {downloadingTemplate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download size={16} className="mr-2" />}
              Télécharger le template CHANTIX (.xlsx)
            </Button>

            {/* Sélection chantier */}
            <div>
              <Label className="text-sm font-medium">Chantier cible</Label>
              <Select value={chantierId} onValueChange={setChantierId}>
                <SelectTrigger className="mt-1" data-testid="import-chantier-select">
                  <SelectValue placeholder="Choisir un chantier..." />
                </SelectTrigger>
                <SelectContent>
                  {(chantiers || []).filter(c => c?.id && c?.name).map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mode */}
            <div>
              <Label className="text-sm font-medium">Mode d'import</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="mt-2">
                <div className="flex items-start gap-2 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer" onClick={() => setMode('add')}>
                  <RadioGroupItem value="add" id="mode-add" data-testid="mode-add-radio" />
                  <div className="flex-1">
                    <Label htmlFor="mode-add" className="cursor-pointer font-medium">Ajouter</Label>
                    <p className="text-xs text-gray-500">Les tâches importées s'ajoutent aux tâches existantes du chantier</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-lg border hover:bg-gray-50 cursor-pointer" onClick={() => setMode('replace')}>
                  <RadioGroupItem value="replace" id="mode-replace" data-testid="mode-replace-radio" />
                  <div className="flex-1">
                    <Label htmlFor="mode-replace" className="cursor-pointer font-medium text-orange-700">Remplacer</Label>
                    <p className="text-xs text-gray-500">Supprime toutes les tâches existantes puis importe (les lots sont conservés)</p>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Upload */}
            <div>
              <Label className="text-sm font-medium">Fichier à importer (.xlsx ou .xml MS Project)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.xml"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm border rounded-lg px-3 py-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#0055FF] file:text-white file:cursor-pointer"
                data-testid="import-file-input"
              />
              {file && (
                <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                  <FileXls size={14} /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              )}
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {result.success ? (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-bold mb-2">
                  <CheckCircle size={20} weight="fill" /> Import réussi
                </div>
                <ul className="text-sm space-y-1 text-gray-700">
                  <li>• {result.tasks_created} tâche(s) importée(s)</li>
                  <li>• {result.lots_created} lot(s) créé(s) automatiquement</li>
                  {result.tasks_deleted > 0 && <li>• {result.tasks_deleted} tâche(s) supprimée(s) (mode Remplacer)</li>}
                </ul>
              </div>
            ) : (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 font-bold mb-2">
                  <X size={20} /> Import échoué
                </div>
                <ul className="text-xs text-red-600 list-disc list-inside space-y-1">
                  {(result.errors || result.parse_errors || []).slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            {result.parse_errors?.length > 0 && result.success && (
              <details className="text-xs">
                <summary className="cursor-pointer text-gray-600">{result.parse_errors.length} ligne(s) ignorée(s)</summary>
                <ul className="mt-2 list-disc list-inside text-gray-500">
                  {result.parse_errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !file || !chantierId || chantierId === 'all'}
                className="bg-[#0055FF] hover:bg-blue-700"
                data-testid="submit-import-btn"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Importer
              </Button>
            </>
          ) : (
            <Button onClick={() => onOpenChange(false)} className="bg-[#0055FF] hover:bg-blue-700">
              Fermer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
